import React, { useEffect, useCallback, useState, useRef } from "react";
import styles from "./PrepareUpdate.module.css";
import {
  integrationsService,
  UserIntegration,
} from "../../services/integrationsService";
import supabaseClient from "../../lib/supabaseClient";
import { Check } from "lucide-react";
import { getIntegrationIconSrc } from "../../utils/integrationIcons";

interface PrepareUpdateProps {
  onNext: (data?: any) => void;
  onBack?: () => void;
}

interface SavedSettings {
  selectedRepositories?: string[];
  selectedKinds?: string[];
  idToNameMap?: Record<string, string>;
}

const PrepareUpdate: React.FC<PrepareUpdateProps> = ({ onNext }) => {
  const [error, setError] = useState<string | null>(null);

  const [generated, setGenerated] = useState<any[] | null>(null);
  const [selectedGenerated, setSelectedGenerated] = useState<Set<number>>(
    new Set(),
  );

  const [progress, setProgress] = useState<number>(0);
  const [progressStep, setProgressStep] = useState<string>("");
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [stepQueue, setStepQueue] = useState<Array<{ step: string; progress: number }>>([]);
  const [streamingInsights, setStreamingInsights] = useState<any[]>([]);
  const [thinkingText, setThinkingText] = useState<string>("");
  const thinkingBoxRef = useRef<HTMLDivElement | null>(null);

  // Local cache keys for generated insights and selected indices
  const LOCAL_GENERATED_KEY = "onboarding_prepare_generated";
  const LOCAL_SELECTED_IDX_KEY = "onboarding_prepare_selected_idx";

  // Track whether we've already kicked off generation to avoid double-fire
  const generationStarted = useRef(false);

  // AbortController ref for cancelling in-progress stream on unmount
  const abortControllerRef = useRef<AbortController | null>(null);

  // Tracks whether a stream is currently in-flight (for the trickle effect)
  const streamingActiveRef = useRef(false);

  // Preloaded integration data — populated immediately on mount so handleAutoGenerate
  // can skip the API round-trips and fire the SSE stream right away.
  const integrationLoadPromiseRef = useRef<Promise<{
    activeIntegrations: UserIntegration[];
    settingsByProvider: Record<string, SavedSettings>;
  }> | null>(null);

  // Restore generated results from localStorage to avoid re-generation on reload
  useEffect(() => {
    try {
      const cached = localStorage.getItem(LOCAL_GENERATED_KEY);
      if (cached) {
        const parsed: any[] = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setGenerated(parsed);
          const selStr = localStorage.getItem(LOCAL_SELECTED_IDX_KEY);
          const allParsedIndices = parsed.map((_: any, i: number) => i);
          if (selStr) {
            try {
              const idxArr: number[] = JSON.parse(selStr);
              const valid = idxArr.filter((n) => Number.isInteger(n));
              // If nothing was explicitly selected, default to all
              setSelectedGenerated(
                new Set(valid.length > 0 ? valid : allParsedIndices),
              );
            } catch (e) {
              // eslint-disable-next-line no-console
              console.debug(
                "PrepareUpdate: failed to parse cached selected indices",
                e,
              );
              setSelectedGenerated(new Set(allParsedIndices));
            }
          } else {
            setSelectedGenerated(new Set(allParsedIndices));
          }
          // Mark generation as done so the auto-generate effect doesn't fire
          generationStarted.current = true;
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug("PrepareUpdate: failed to read local cache", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abort any in-progress stream on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Preload integration data immediately on mount so handleAutoGenerate can skip
  // the API calls. Both the preload and handleAutoGenerate share the same Promise,
  // so only one network round-trip happens even when they race.
  useEffect(() => {
    if (integrationLoadPromiseRef.current) return;
    integrationLoadPromiseRef.current = (async () => {
      const list = await integrationsService.getUserIntegrations();
      const activeIntegrations = (list || []).filter(
        (i: any) => i.integration_status === "active",
      );
      const providers = Array.from(
        new Set(
          activeIntegrations.map((i: UserIntegration) =>
            (i.provider || "").toLowerCase(),
          ),
        ),
      );
      const settingsResults = await Promise.all(
        providers.map(async (p) => {
          try {
            const settings = await integrationsService.getIntegrationSettings(p);
            return [p, settings || {}] as const;
          } catch {
            return [p, {}] as const;
          }
        }),
      );
      const settingsByProvider: Record<string, SavedSettings> = {};
      settingsResults.forEach(([p, s]) => (settingsByProvider[p] = s));
      return { activeIntegrations, settingsByProvider };
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll thinkingBox to bottom on each new token
  useEffect(() => {
    if (thinkingBoxRef.current) {
      thinkingBoxRef.current.scrollTop = thinkingBoxRef.current.scrollHeight;
    }
  }, [thinkingText]);

  // Trickle: slowly advance the progress bar while streaming so it never
  // appears frozen. Caps at 89% so server events (step=90, complete=100)
  // still produce visible jumps. Rate decelerates as it approaches the cap.
  useEffect(() => {
    const interval = setInterval(() => {
      if (!streamingActiveRef.current) return;
      setProgress((prev) => {
        if (prev >= 95) return prev;
        const gap = 95 - prev;
        return prev + Math.max(0.1, gap * 0.005);
      });
    }, 75);
    return () => clearInterval(interval);
  }, []);

  // Step queue drain: show each queued step with varied ~2s delays so the
  // loading sequence feels deliberate even though SSE events arrive instantly.
  // Delays (ms) per position: first step appears quickly, rest ~2s apart.
  const STEP_DRAIN_DELAYS = [350, 2050, 1800, 2250];
  useEffect(() => {
    if (stepQueue.length === 0) return;
    const delay = STEP_DRAIN_DELAYS[Math.min(completedSteps.length, STEP_DRAIN_DELAYS.length - 1)];
    const timer = setTimeout(() => {
      const item = stepQueue[0];
      setCompletedSteps(prev =>
        prev[prev.length - 1] !== item.step ? [...prev, item.step] : prev,
      );
      setProgress(prev => Math.max(prev, item.progress));
      setProgressStep(item.step);
      setStepQueue(prev => prev.slice(1));
    }, delay);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepQueue, completedSteps.length]);

  // Auto-generate: load integrations + settings, then stream insights
  const handleAutoGenerate = useCallback(async () => {
    streamingActiveRef.current = true;
    setError(null);
    setProgress(0);
    setProgressStep("");
    setCompletedSteps([]);
    setStepQueue([]);
    setStreamingInsights([]);
    setThinkingText("");

    try {
      // Use the preloaded promise (started on mount) so we don't block the SSE
      // stream behind fresh API calls. On retries the promise is already resolved
      // and awaiting it is instant.
      let activeIntegrations: UserIntegration[];
      let settingsByProvider: Record<string, SavedSettings>;

      if (integrationLoadPromiseRef.current) {
        ({ activeIntegrations, settingsByProvider } =
          await integrationLoadPromiseRef.current);
      } else {
        // Fallback: load directly (should rarely happen)
        const list = await integrationsService.getUserIntegrations();
        activeIntegrations = (list || []).filter(
          (i: any) => i.integration_status === "active",
        );
        const providers = Array.from(
          new Set(
            activeIntegrations.map((i: UserIntegration) =>
              (i.provider || "").toLowerCase(),
            ),
          ),
        );
        const settingsResults = await Promise.all(
          providers.map(async (p) => {
            try {
              const settings =
                await integrationsService.getIntegrationSettings(p);
              return [p, settings || {}] as const;
            } catch {
              return [p, {}] as const;
            }
          }),
        );
        settingsByProvider = {};
        settingsResults.forEach(([p, s]) => (settingsByProvider[p] = s));
      }

      if (activeIntegrations.length === 0) {
        throw new Error(
          "No active integrations found. Please go back and connect at least one integration.",
        );
      }

      // Build dataSelections from saved settings
      const dataSelections: Record<
        string,
        { selectedIds?: string[]; kinds?: string[] }
      > = {};
      for (const integration of activeIntegrations) {
        const p = (integration.provider || "").toLowerCase();
        const settings = settingsByProvider[p];
        if (settings) {
          const entry: { selectedIds?: string[]; kinds?: string[] } = {};
          if (
            settings.selectedRepositories &&
            settings.selectedRepositories.length > 0
          ) {
            entry.selectedIds = settings.selectedRepositories;
          }
          if (settings.selectedKinds && settings.selectedKinds.length > 0) {
            entry.kinds = settings.selectedKinds;
          }
          if (entry.selectedIds || entry.kinds) {
            dataSelections[integration.id] = entry;
          }
        }
      }

      // Default to past 30 days
      const defaultStartDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const today = new Date();

      const payload: Record<string, unknown> = {
        type: "comparison",
        category: "operational",
        data_sources: activeIntegrations.map(
          (i: UserIntegration) => i.provider,
        ),
        integrationIds: activeIntegrations.map((i: UserIntegration) => i.id),
        timeRange: {
          start: defaultStartDate.toISOString(),
          end: new Date(
            `${today.toISOString().slice(0, 10)}T23:59:59.999Z`,
          ).toISOString(),
        },
        dataSelections:
          Object.keys(dataSelections).length > 0 ? dataSelections : undefined,
      };

      // ── SSE streaming ──────────────────────────────────────────────────────
      const { data: sessionData } = await supabaseClient.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        setError("Authentication required. Please sign in again.");
        return;
      }

      const API_BASE_URL =
        process.env.REACT_APP_API_URL ||
        (window.location.hostname === "localhost"
          ? "http://localhost:4000"
          : "https://api.sendupdates.ai");

      const controller = new AbortController();
      abortControllerRef.current = controller;
      const streamTimeout = setTimeout(() => controller.abort(), 500000);

      let response: Response;
      try {
        response = await fetch(`${API_BASE_URL}/insights/generate-stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
      } catch (fetchErr: any) {
        clearTimeout(streamTimeout);
        if (fetchErr?.name === "AbortError") {
          setError("Insight generation timed out. Please retry.");
        } else {
          setError(fetchErr?.message || "Failed to generate insights");
        }
        return;
      }

      if (!response.ok) {
        clearTimeout(streamTimeout);
        setError(`HTTP error ${response.status}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        clearTimeout(streamTimeout);
        setError("No response body from server");
        return;
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let currentEvent = "";
      let currentData: any = null;
      let receivedComplete = false;

      try {
        let streamDone = false;
        while (!streamDone) {
          const { done, value } = await reader.read();
          streamDone = done;
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const rawLine of lines) {
            const line = rawLine.replace(/\r$/, ""); // strip CR if present

            if (line === "") {
              // Blank line: dispatch buffered event
              if (currentEvent && currentData !== null) {
                if (currentEvent === "step") {
                  const stepText = currentData.step ?? "";
                  const stepProgress = currentData.progress ?? 0;
                  if (stepText) {
                    setStepQueue(prev => [
                      ...prev,
                      { step: stepText, progress: stepProgress },
                    ]);
                  }
                } else if (currentEvent === "ping") {
                  // keepalive no-op
                } else if (currentEvent === "insight") {
                  if (currentData?.insight) {
                    const insight = currentData.insight;
                    setStreamingInsights((prev) => [...prev, insight]);
                  }
                } else if (currentEvent === "thinking") {
                  if (currentData?.text) {
                    const text = currentData.text;
                    setThinkingText((prev) => prev + text);
                  }
                } else if (currentEvent === "complete") {
                  const items: any[] = (currentData.data?.insights ||
                    (currentData.data?.insight
                      ? [currentData.data.insight]
                      : [])) as any[];
                  setGenerated(items);
                  const allIndices = items.map((_: any, i: number) => i);
                  setSelectedGenerated(new Set(allIndices));
                  setStreamingInsights([]);
                  setThinkingText("");
                  try {
                    localStorage.setItem(
                      LOCAL_GENERATED_KEY,
                      JSON.stringify(items),
                    );
                    localStorage.setItem(
                      LOCAL_SELECTED_IDX_KEY,
                      JSON.stringify(allIndices),
                    );
                  } catch {
                    // eslint-disable-next-line no-console
                    console.debug(
                      "PrepareUpdate: failed to cache generated insights",
                    );
                  }
                  receivedComplete = true;
                } else if (currentEvent === "error") {
                  setError(currentData.error || "Failed to generate insights");
                  receivedComplete = true; // treat as terminal
                }
              }
              currentEvent = "";
              currentData = null;
            } else if (line.startsWith("event:")) {
              currentEvent = line.substring(6).trim();
            } else if (line.startsWith("data:")) {
              try {
                currentData = JSON.parse(line.substring(5).trim());
              } catch {
                // malformed data line — skip silently (REQ-F7)
              }
            }
          }
        }
      } catch (readErr: any) {
        if (readErr?.name === "AbortError") {
          setError("Insight generation timed out. Please retry.");
        } else {
          setError(readErr?.message || "Failed to generate insights");
        }
        return; // prevents if (!receivedComplete) from overwriting the specific error above
      } finally {
        clearTimeout(streamTimeout);
      }

      // If stream ended without a complete or error event (REQ-F8)
      // Only reached when the while loop exits cleanly (done = true, no exception).
      if (!receivedComplete) {
        setError("Insight generation ended unexpectedly. Please retry.");
      }
      // ── End SSE streaming ──────────────────────────────────────────────────
    } catch (e: any) {
      setError(e?.message || "Failed to generate insights");
    } finally {
      streamingActiveRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // no deps — all refs are stable; integrations service is a singleton

  // Fire auto-generate once on mount (unless we restored from cache)
  useEffect(() => {
    if (generationStarted.current) return;
    generationStarted.current = true;
    handleAutoGenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderInsightCard = (g: any, idx: number, isStreaming = false) => {
    const isSelected = selectedGenerated.has(idx);
    const providers = Array.isArray(g?.data_sources)
      ? g.data_sources
      : g?.data_sources
        ? [g.data_sources]
        : [];
    const toggle = () =>
      setSelectedGenerated((prev) => {
        const next = new Set(prev);
        if (next.has(idx)) next.delete(idx);
        else next.add(idx);
        return next;
      });
    return (
      <div
        key={isStreaming ? `streaming-${idx}` : idx}
        className={`${styles.card} ${styles.cardSelectable} ${
          isSelected ? styles.cardSelected : ""
        } ${isStreaming ? styles.cardFadeIn : ""}`}
        onClick={toggle}
      >
        <div
          className={`${styles.check} ${
            isSelected ? styles.checkSelected : ""
          }`}
        >
          {isSelected ? (
            <Check size={14} />
          ) : (
            <span style={{ fontSize: 12 }} />
          )}
        </div>
        <div className={styles.cardHeader}>
          <div
            className={`${styles.cardTitle} ${styles.cardTitleReserve}`}
          >
            {g?.title || g?.summary?.headline || "Insight"}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 2,
            flexWrap: "wrap",
          }}
        >
          {providers.map((p: any, i: number) => {
            const key = String(p || "").toLowerCase();
            const src = getIntegrationIconSrc(key) as string | null;
            if (!src) return null;
            return (
              <img
                key={`${key}-${i}`}
                src={src}
                alt={key}
                style={{ width: 16, height: 16, objectFit: "contain" }}
              />
            );
          })}
        </div>
        <div style={{ color: "#e5e7eb", fontSize: 14, marginTop: 8 }}>
          {g?.summary?.narrative ||
            g?.description ||
            "No description provided."}
        </div>
      </div>
    );
  };

  const handleDebugReset = useCallback(() => {
    try {
      localStorage.removeItem(LOCAL_GENERATED_KEY);
      localStorage.removeItem(LOCAL_SELECTED_IDX_KEY);
    } catch { /* ignore */ }
    setGenerated(null);
    setSelectedGenerated(new Set());
    setStreamingInsights([]);
    setThinkingText("");
    setCompletedSteps([]);
    setStepQueue([]);
    setProgress(0);
    setProgressStep("");
    setError(null);
    generationStarted.current = false;
    handleAutoGenerate();
  }, [handleAutoGenerate]);

  return (
    <div className={styles.root}>
      <h2 className={styles.header}>Prepare Your First Update</h2>
      <p className={styles.subheader}>
        We&apos;re analyzing your connected integrations from the past 30 days.
        Select the insights you&apos;d like to include in your email.
      </p>

      {error && (
        <div className={styles.error}>
          {error}
          <div className={styles.footer} style={{ marginTop: 16 }}>
            <button
              className={`${styles.generateButton} ${styles.generatePrimary}`}
              onClick={() => {
                generationStarted.current = false;
                handleAutoGenerate();
              }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {!error && !generated && (
        <>
          <div className={styles.stepList}>
            {completedSteps.length === 0 ? (
              <div className={styles.stepRowActive}>
                <span className={styles.stepPrefix}>[►]</span>
                <span>{"loading your integrations..."}</span>
                <span className={styles.cursor} />
              </div>
            ) : (
              completedSteps.map((step, i) => {
                const isActive = !generated && i === completedSteps.length - 1;
                return (
                  <div
                    key={i}
                    className={isActive ? styles.stepRowActive : styles.stepRowDone}
                  >
                    <span className={styles.stepPrefix}>
                      {isActive ? "[►]" : "[✓]"}
                    </span>
                    <span>{step}</span>
                    {isActive && <span className={styles.cursor} />}
                  </div>
                );
              })
            )}
          </div>
          {thinkingText && (
            <div className={styles.thinkingBox} ref={thinkingBoxRef}>
              <pre className={styles.thinkingText}>{thinkingText}</pre>
            </div>
          )}
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>
        </>
      )}

      {!generated && !error && streamingInsights.length > 0 && (
        <div className={styles.grid} style={{ marginBottom: 16 }}>
          {streamingInsights.map((g, idx) => renderInsightCard(g, idx, true))}
        </div>
      )}

      {generated && (
        <div className={styles.grid}>
          {generated.map((g, idx) => renderInsightCard(g, idx, false))}
          <div className={`${styles.footer} ${styles.gridFooter}`}>
            <button
              className={`${styles.generateButton} ${styles.generatePrimary}`}
              disabled={selectedGenerated.size === 0}
              style={{ width: "100%", gridColumn: "1 / -1" }}
              onClick={() => {
                if (!generated) return;
                const chosen = generated.filter((_, i) =>
                  selectedGenerated.has(i),
                );
                try {
                  localStorage.setItem(
                    LOCAL_SELECTED_IDX_KEY,
                    JSON.stringify(Array.from(selectedGenerated)),
                  );
                } catch (e) {
                  // eslint-disable-next-line no-console
                  console.debug(
                    "PrepareUpdate: failed to cache selected indices",
                    e,
                  );
                }
                onNext({ selectedInsights: chosen });
              }}
            >
              Preview Email
            </button>
          </div>
        </div>
      )}
      {process.env.NODE_ENV === "development" && (
        <button
          style={{
            marginTop: 8,
            padding: "6px 10px",
            background: "transparent",
            border: "1px solid #3f3f46",
            color: "#6b7280",
            cursor: "pointer",
            fontSize: 11,
            alignSelf: "flex-start",
          }}
          onClick={handleDebugReset}
        >
          [dev] reset &amp; retry
        </button>
      )}
    </div>
  );
};

export default PrepareUpdate;
